import { useGetQuestionDetailsV1HealthDataSlugQuestionDetailsGet } from '@/api/generated-catch-backend/health-data/health-data';
import {
  HealthDataPoint,
  QuestionnaireCategoryItemsItemOneOf as Question,
  QuestionnaireResponseQuestionResponse,
  UpdateHealthDataProfileResponse,
} from '@/api/generated-catch-backend/model';
import { QuestionnaireEditModal } from '@/app/components/questionnaireV2/components/questionnaire-edit-modal/questionnaire-edit-modal';
import ChevronDown from '@/app/components/questionnaireV2/icons/chevron-down';
import { WarningIcon } from '@/app/components/questionnaireV2/icons/warning-icon';
import { EditIcon } from '@/components/icons/edit-icon';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'motion/react';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CategoryCardBase from './category-card-base';
import { ExpandedDataCategoryCardProps } from './types';

function isValidQuestionResponse(
  data: QuestionnaireResponseQuestionResponse | undefined,
): data is QuestionnaireResponseQuestionResponse & { question: Question } {
  return (
    !!data?.question && 'questionnaireItemKind' in data.question && data.question.questionnaireItemKind === 'question'
  );
}

const QuestionRow = React.forwardRef<
  HTMLDivElement,
  { dataPoint: HealthDataPoint; onEdit: (slug: string) => void; isLoading: boolean }
>(({ dataPoint, onEdit, isLoading }, forwardedRef) => {
  return (
    <div ref={forwardedRef} className="flex items-start gap-4 py-3">
      {/* Warning Icon or Spacer */}
      {dataPoint.requiresAttention && (
        <div className="flex w-8 items-center">{dataPoint.requiresAttention && <WarningIcon />}</div>
      )}
      {/* Question Content */}
      <div className="flex flex-1 flex-col gap-1">
        <p className="text-xs leading-[18px] font-normal tracking-[0.2px] text-[#183344]">{dataPoint.displayName}</p>
        <p className="text-base leading-6 font-medium text-[#183344]">
          {dataPoint.value && dataPoint.unit
            ? `${dataPoint.value} ${dataPoint.unit}`
            : dataPoint.value || 'Not provided'}
        </p>
      </div>

      {/* Edit Icon / Loading Spinner - Only shown if data point is editable */}
      {dataPoint.isEditable && (
        <div className="flex items-center">
          <button
            onClick={() => onEdit(dataPoint.slug)}
            className="flex items-center justify-center rounded-lg p-2 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={`Edit ${dataPoint.displayName}`}
            type="button"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-[#183344]" />
            ) : (
              <EditIcon />
            )}
          </button>
        </div>
      )}
    </div>
  );
});

QuestionRow.displayName = 'QuestionRow';

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="border-t border-[#d4c2b5] pt-6 pb-3">
      <h4 className="text-xl leading-[1.3] font-bold text-[#183344]">{title}</h4>
    </div>
  );
}

export const ExpandedDataCategoryCard = memo(function ExpandedDataCategoryCard({
  name,
  description,
  icon,
  progressValue,
  progressMax,
  lastUpdated,
  isComplete,
  subcategories,
  isExpanded,
  onToggleExpanded,
}: ExpandedDataCategoryCardProps) {
  const queryClient = useQueryClient();
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [pendingNextSlug, setPendingNextSlug] = useState<string | null>(null);
  const [followUpQueue, setFollowUpQueue] = useState<string[]>([]);
  const editedSlugsRef = useRef<Set<string>>(new Set());
  const dataPointRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const categoryCardRef = useRef<HTMLDivElement>(null);

  const editingDataPoint = useMemo(() => {
    if (!editingSlug) return null;
    return subcategories.flatMap((sub) => sub.dataPoints).find((dp) => dp.slug === editingSlug);
  }, [editingSlug, subcategories]);

  // Data point for the pending next question (used for pre-fetching)
  const pendingDataPoint = useMemo(() => {
    if (!pendingNextSlug) return null;
    return subcategories.flatMap((sub) => sub.dataPoints).find((dp) => dp.slug === pendingNextSlug);
  }, [pendingNextSlug, subcategories]);

  // Fetch question details for the CURRENT editing slug
  const questionDetailsQuery = useGetQuestionDetailsV1HealthDataSlugQuestionDetailsGet(
    editingSlug || '',
    { 'question-slug': editingDataPoint?.sourceId || '' },
    {
      query: {
        enabled: !!editingSlug && !!editingDataPoint?.sourceId,
        retry: 1,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
      },
    },
  );

  // Pre-fetch question details for the NEXT question (while still showing current)
  const pendingQuestionQuery = useGetQuestionDetailsV1HealthDataSlugQuestionDetailsGet(
    pendingNextSlug || '',
    { 'question-slug': pendingDataPoint?.sourceId || '' },
    {
      query: {
        enabled: !!pendingNextSlug && !!pendingDataPoint?.sourceId,
        retry: 1,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
      },
    },
  );

  // Extract validated question data for the modal (with proper typing)
  const validatedQuestionData = useMemo(() => {
    if (!isValidQuestionResponse(questionDetailsQuery.data)) return null;
    return questionDetailsQuery.data;
  }, [questionDetailsQuery.data]);

  // When pending question data is ready, transition to it
  useEffect(() => {
    if (pendingNextSlug && isValidQuestionResponse(pendingQuestionQuery.data) && !pendingQuestionQuery.isFetching) {
      // Data is cached, now safe to swap - query will use cached data instantly
      setEditingSlug(pendingNextSlug);
      setPendingNextSlug(null);
    }
  }, [pendingNextSlug, pendingQuestionQuery.data, pendingQuestionQuery.isFetching]);

  const handleEditDataPoint = (slug: string) => {
    setEditingSlug(slug);
    // Modal will open automatically when data is loaded (see below)
  };

  const handleEditComplete = useCallback(
    (response?: UpdateHealthDataProfileResponse) => {
      // Helper to remove all edited question caches
      // We use removeQueries (not invalidateQueries) because:
      // - invalidateQueries marks data as stale but keeps it in cache
      // - React Query returns stale cached data while refetching
      // - useForm's defaultValues only apply on mount, not on data updates
      // - So the form would show stale data even after refetch completes
      // By removing the query entirely, we force a fresh fetch with no stale data
      const clearEditedQuestionCaches = () => {
        editedSlugsRef.current.forEach((slug) => {
          queryClient.removeQueries({
            predicate: (query) => {
              const key = query.queryKey[0];
              return typeof key === 'string' && key === `/v1/health-data/${slug}/question-details`;
            },
          });
        });
        editedSlugsRef.current.clear();
      };

      // If no response (user clicked X to close), clear everything
      if (!response) {
        clearEditedQuestionCaches();
        setFollowUpQueue([]);
        setEditingSlug(null);
        setPendingNextSlug(null);
        return;
      }

      // Track that this question was edited (for cache invalidation later)
      if (editingSlug) {
        editedSlugsRef.current.add(editingSlug);
      }

      // Get newly unlocked HDP slugs from the response
      const newlyUnlocked = response.metadata?.unlockedHdpSlugs ?? [];

      // Combine new unlocked slugs with existing queue
      const combinedQueue = [...newlyUnlocked, ...followUpQueue];

      if (combinedQueue.length > 0) {
        // Start pre-fetching next question (don't change editingSlug yet!)
        // The modal stays on current question with button loading
        // When data is ready, useEffect will swap editingSlug
        const [nextSlug, ...remainingQueue] = combinedQueue;
        setFollowUpQueue(remainingQueue);
        setPendingNextSlug(nextSlug);
      } else {
        // Queue empty, close the modal and clear caches
        clearEditedQuestionCaches();
        setEditingSlug(null);
      }
    },
    [followUpQueue, editingSlug, queryClient],
  );

  return (
    <div ref={categoryCardRef}>
      <CategoryCardBase
        name={name}
        description={description}
        icon={icon}
        progressValue={progressValue}
        progressMax={progressMax}
        lastUpdated={lastUpdated}
        isComplete={isComplete}
      >
        {/* Edit Button */}
        <motion.button
          onClick={onToggleExpanded}
          className="relative box-border flex h-[40px] w-full shrink-0 items-center justify-center rounded-[100px] bg-[#183344] px-[20px] lg:max-w-72"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          transition={{ duration: 0.2 }}
          aria-expanded={isExpanded}
          aria-controls={`${name.toLowerCase().replace(/\s+/g, '-')}-content`}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${name} details`}
          type="button"
        >
          <span className="text-[14px] leading-[1.4] font-bold text-white">Edit</span>
          <motion.div
            className="absolute right-5 h-4 w-4 text-white"
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className="h-full w-full" />
          </motion.div>
        </motion.button>

        {/* Accessibility Live Region */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {isExpanded ? `${name} expanded` : `${name} collapsed`}
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{
              duration: 0.3,
              ease: 'easeInOut',
            }}
            className="w-full overflow-hidden pt-[18px]"
            id={`${name.toLowerCase().replace(/\s+/g, '-')}-content`}
            role="region"
            aria-label={`${name} details`}
          >
            <div className="flex w-full flex-col">
              {subcategories.map((subcategory, subcategoryIndex) => (
                <div key={subcategory.id} className="flex w-full flex-col">
                  {/* Subcategory Header - skip if name is "-" */}
                  <SectionHeader title={subcategory.name === '-' ? '' : subcategory.name} />

                  {/* Data Points in Subcategory */}
                  <div className="flex flex-col">
                    {subcategory.dataPoints.map((dataPoint, dataPointIndex) => (
                      <div key={dataPoint.id}>
                        <QuestionRow
                          dataPoint={dataPoint}
                          onEdit={handleEditDataPoint}
                          isLoading={editingSlug === dataPoint.slug && questionDetailsQuery.isLoading}
                          ref={(element) => {
                            if (element) {
                              dataPointRefs.current.set(dataPoint.slug, element);
                            } else {
                              dataPointRefs.current.delete(dataPoint.slug);
                            }
                          }}
                        />
                        {/* Add bottom border except for last data point in last subcategory */}
                        {!(
                          subcategoryIndex === subcategories.length - 1 &&
                          dataPointIndex === subcategory.dataPoints.length - 1
                        ) && <div className="h-px w-full" />}
                      </div>
                    ))}
                  </div>

                  {/* Subcategory bottom border - skip if name is "-" or if last subcategory */}
                  {subcategory.name !== '-' && subcategoryIndex < subcategories.length - 1 && (
                    <div className="border-b border-[#d4c2b5] pb-6" />
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Edit Modal - stays mounted during question transitions */}
        {editingSlug && validatedQuestionData && (
          <QuestionnaireEditModal
            currentAnswer={validatedQuestionData.currentAnswer ?? null}
            question={validatedQuestionData.question}
            healthDataPointSlug={editingSlug}
            title="Edit"
            subtitle={editingDataPoint?.displayName || editingSlug}
            onClose={handleEditComplete}
            isOpen={true}
            isTransitioningToNext={!!pendingNextSlug}
          />
        )}

        {/* Error state */}
        {editingSlug && questionDetailsQuery.isError && (
          <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black">
            <div className="rounded-lg bg-white p-6">
              <div className="text-center text-red-600">Failed to load health data point. Please try again later.</div>
              <button
                onClick={() => handleEditComplete()}
                className="mt-4 rounded bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
                type="button"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </CategoryCardBase>
    </div>
  );
});

export default ExpandedDataCategoryCard;
